import DOMPurify from "dompurify";
import React, { useContext, useState } from "react";
import { Badge, Button, Card, Col, Container, Modal, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Bs0CircleFill, BsCalendarDate, BsClipboardData, BsCreditCard, BsPeople } from "react-icons/bs";
import { FaCheckCircle, FaDownload, FaFileMedical, FaInfoCircle, FaMoneyBill, FaTrash } from "react-icons/fa";
import { GiMoneyStack } from "react-icons/gi";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import ConfirmModal from "../components/ConfirmModal";
import { EditableModal } from "../components/editableModal";
import AppContext from "../context/app-context";
import userProfileService from "../services/userProfileService";
import ExpenseTrackingMetadataDB from "../storage/ExpenseTrackingMetadataDB";
import { getExpenseFieldsFromProfile } from "../utils/helperFunctions";

const ExpenseDetailPage = () => {
  const { id } = useParams("id");
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userProfile, setLoading, loadItems = () => {}, trackingItems = [], onlineItems = [] } = useContext(AppContext);
  const expenses = trackingItems.filter((item) => item.expenseId === id);
  const { fieldConfigs = [] } = userProfile || {};
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showHtmlModal, setShowHtmlModal] = useState(false);
  if (expenses.length !== 1) {
    // navigate("/ExpenseTracking");
    return;
  }

  const expense = expenses[0];
  const {
    attachments = [],
    items = [],
    version,
    location,
    createdDate,
    total = 0,
    subtotal,
    hsaEligible,
    vendor,
    userId,
    purchasedDate,
    category,
    expenseStatus,
    paymentMethod,
    lastModifiedDate,
    orderId,
    title,
    expenseId,
    discountTotal,
    tax,
    currency,
    htmlBody,
    textBody,
    ...customAttributes
  } = expense;
  const customKeys = Object.keys(customAttributes);
  const handleDownload = async (attachment) => {
    try {
      // Replace with actual download logic
      const { key } = attachment;
      userProfileService.getExpenseAttachmentUrl({
        key,
        callback: async ({ error, data: { downloadUrl } = {} }) => {
          if (error || !downloadUrl) return;
          // window.open(downloadUrl);
          // console.log(downloadUrl);

          const response = await fetch(downloadUrl);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = attachment.name;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        },
      });
    } catch (error) {
      console.error("Download failed:", error);
      alert("Download failed. Please try again.");
    }
  };
  const hasDiscount = items.find((item) => item.discount);
  const hasUnitPrice = items.find((item) => item.unit_price);
  const hashsaEligible = items.find((item) => item.hsaEligible);

  return (
    <Container className="py-4" style={{ maxWidth: "1200px" }}>
      {/* Header Section */}
      <Row className="mb-4">
        <Col>
          <h2 className="text-success mb-3">
            <FaMoneyBill className="me-2" />
            {title}
          </h2>
          <div className="d-flex align-items-center gap-3">
            <Badge bg="success" className="fs-6">
              <FaCheckCircle className="me-2" />
              {t(`ExpenseDetailPage.status.${expenseStatus}`)}
            </Badge>
            <span className="text-muted">
              <BsCalendarDate className="me-2" />
              {new Date(purchasedDate).toLocaleDateString()}
            </span>
          </div>
        </Col>
        <Col style={{ margin: "10px 0" }}>
          <div className="d-flex gap-2">
            <EditableModal
              title={t("ExpenseDetailPage.header.updateButton")}
              configs={getExpenseFieldsFromProfile(userProfile)}
              actionLabel={t("ExpenseDetailPage.header.updateButton")}
              currentValue={expense}
              actionOnClick={async (data) => {
                const { expenseId, version } = expense;
                const updatedItem = { expenseId, version, ...data, lastModifiedDate: new Date().toISOString() };
                userProfileService.putExpenseEvent({
                  updatedItem: updatedItem,
                  callback: async ({ err, data }) => {
                    if (err) {
                      toast.error(err);
                      return;
                    }
                    const { expenseResource } = data;
                    await ExpenseTrackingMetadataDB.putItemToExpenseTrackingMetadata({
                      ...expense,
                      ...expenseResource,
                    });
                    toast.info(`Successfully updated item.`);
                    loadItems();
                  },
                });
              }}
            />
            <Button
              variant="outline-danger"
              className="d-flex align-items-center gap-2 px-3"
              onClick={() => setShowDeleteModal(true)}
              style={{
                transition: "all 0.3s ease",
                borderWidth: "2px",
                fontWeight: 500,
              }}
            >
              <FaTrash className="me-1" />
              {t("ExpenseDetailPage.header.deleteButton")}
            </Button>
          </div>
        </Col>
      </Row>

      {/* Details Cards */}
      <Row className="g-4 mb-4">
        <Col md={6}>
          <Card className="h-100 shadow-sm border-success">
            <Card.Body>
              <Card.Title className="text-success">
                <GiMoneyStack className="me-2" />
                {t("ExpenseDetailPage.paymentDetails.title")}
              </Card.Title>
              <Table borderless className="mb-0">
                <tbody>
                  <tr>
                    <td>{t("ExpenseDetailPage.paymentDetails.totalAmount")}:</td>
                    <td className="fw-bold">${total.toFixed(2)}</td>
                  </tr>
                  {Boolean(tax && tax > 0) && (
                    <tr>
                      <td>{t("ExpenseDetailPage.paymentDetails.tax")}</td>
                      <td>$ {tax}</td>
                    </tr>
                  )}
                  {vendor && (
                    <tr>
                      <td>{t("ExpenseDetailPage.paymentDetails.vendor")}</td>
                      <td>
                        <BsPeople className="me-2" />
                        {vendor}
                      </td>
                    </tr>
                  )}
                  {orderId && (
                    <tr>
                      <td>{t("ExpenseDetailPage.paymentDetails.orderNumber")}</td>
                      <td>
                        <Bs0CircleFill className="me-2" />
                        {orderId}
                      </td>
                    </tr>
                  )}
                  {paymentMethod && (
                    <tr>
                      <td>{t("ExpenseDetailPage.paymentDetails.paymentMethod")}:</td>
                      <td>
                        <BsCreditCard className="me-2" />
                        {paymentMethod}
                      </td>
                    </tr>
                  )}
                  {location && (
                    <tr>
                      <td>{t("ExpenseDetailPage.paymentDetails.location")}:</td>
                      <td>{location}</td>
                    </tr>
                  )}
                  <tr>
                    <td>{t("ExpenseDetailPage.paymentDetails.category")}:</td>
                    <td>
                      <Badge bg="success">{category}</Badge>
                    </td>
                  </tr>
                  {hsaEligible && (
                    <tr>
                      <td>{t("ExpenseDetailPage.paymentDetails.hsaEligible")}:</td>
                      <td>
                        <Badge bg="success">{hsaEligible}</Badge>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="h-100 shadow-sm border-success">
            <Card.Body>
              <Card.Title className="text-success">
                <FaFileMedical className="me-2" />
                {t("ExpenseDetailPage.otherInfo.title")}
              </Card.Title>
              <div>
                <Table borderless className="mb-0">
                  <tbody>
                    {customKeys.map((keyName) => {
                      const keyValue = customAttributes[keyName];
                      const customField = fieldConfigs.find(
                        (item) => item.fieldName.toLowerCase() === keyName.toLowerCase()
                      );
                      var displayName = keyName;
                      if (customField) {
                        displayName = customField.label;
                      }
                      return (
                        <tr key={keyName}>
                          <td>{displayName}</td>
                          <td>
                            <BsClipboardData className="me-2" />
                            {keyValue}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              <Card.Title className="text-success">
                <FaFileMedical className="me-2" />
                {t("ExpenseDetailPage.otherInfo.attachments")}
              </Card.Title>
              {htmlBody && (
                <Button variant="outline-success" size="sm" onClick={() => setShowHtmlModal(true)}>
                  {t("ExpenseDetailPage.otherInfo.viewOriginal")}
                </Button>
              )}
              {attachments.length > 0 && (
                <div className="list-group">
                  {attachments.map((attachment, index) => (
                    <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                      <div>
                        <span className="me-2">{attachment.name}</span>
                        <Badge
                          bg={
                            attachment.status === "UploadComplete"
                              ? "success"
                              : attachment.status === "Processing"
                              ? "warning"
                              : "danger"
                          }
                        >
                          {/* {t(`ExpenseDetailPage.statusLabels.${attachment.status?.toLowerCase()}`)} */}
                          {attachment.status?.toLowerCase()}
                        </Badge>
                      </div>
                      <Button variant="outline-success" size="sm" onClick={() => handleDownload(attachment)}>
                        <FaDownload />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Items Table */}
      {items.length > 0 && (
        <Card className="shadow-sm border-success">
          <Card.Body>
            <Card.Title className="text-success mb-4">
              <FaInfoCircle className="me-2" />
              {t("ExpenseDetailPage.itemsTable.title")}
            </Card.Title>
            <div className="table-responsive">
              <Table striped hover className="mb-0">
                <thead className="bg-success text-white">
                  <tr>
                    <th>{t("ExpenseDetailPage.itemsTable.procedure")}</th>
                    <th className="text-end">{t("ExpenseDetailPage.itemsTable.price")}</th>
                    <th className="text-end">{t("ExpenseDetailPage.itemsTable.quantity")}</th>
                    {hasUnitPrice && <th className="text-end">{t("ExpenseDetailPage.itemsTable.unitPrice")}</th>}
                    {hasDiscount && <th className="text-end">{t("ExpenseDetailPage.itemsTable.discount")}</th>}
                    <th className="text-end">{t("ExpenseDetailPage.itemsTable.total")}</th>
                    {hashsaEligible && <th className="text-end">{t("ExpenseDetailPage.itemsTable.hsa")}</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => {
                    const { unit_price, discount } = item;
                    return (
                      <tr key={index}>
                        <td>{item.name}</td>
                        <td className="text-end">${item.price.toFixed(2)}</td>
                        <td className="text-end">{item.quantity}</td>
                        {hasUnitPrice && <td className="text-end ">{unit_price && `$${unit_price.toFixed(2)}`}</td>}
                        {hasDiscount && <td className="text-end ">{discount && `$${discount.toFixed(2)}`}</td>}
                        <td className="text-end ">${item.price.toFixed(2)}</td>
                        {hashsaEligible && <td className="text-end fw-bold">{item.hsaEligible}</td>}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-light">
                  {Boolean(discountTotal) && (
                    <tr>
                      <td colSpan="3" className="text-end fw-bold">
                        {t("ExpenseDetailPage.itemsTable.discountTotal")}:
                      </td>
                      <td className="text-end fw-bold">- ${discountTotal?.toFixed(2)}</td>
                    </tr>
                  )}
                  {Boolean(subtotal) && (
                    <tr>
                      <td colSpan="3" className="text-end fw-bold">
                        {t("ExpenseDetailPage.itemsTable.subtotal")}:
                      </td>
                      <td className="text-end fw-bold">${subtotal?.toFixed(2)}</td>
                    </tr>
                  )}
                  {Boolean(tax && tax > 0) && (
                    <tr>
                      <td colSpan="3" className="text-end fw-bold">
                        {t("ExpenseDetailPage.itemsTable.tax")}:
                      </td>
                      <td className="text-end fw-bold">${tax?.toFixed(2)}</td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan="3" className="text-end fw-bold">
                      {t("ExpenseDetailPage.itemsTable.total")}:
                    </td>
                    <td className="text-end fw-bold">${total?.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}
      {/* <div className="d-md-none fixed-top p-3">
        <Button variant="success" size="lg" className="w-100 shadow-lg" onClick={() => window.print()}>
          <FaDownload className="me-2" />
          Download Receipt
        </Button>
      </div> */}
      <Modal show={showHtmlModal} onHide={() => setShowHtmlModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("ExpenseDetailPage.modals.originalReceipt")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            className="border p-3"
            style={{
              maxHeight: "70vh",
              overflow: "auto",
              fontSize: "0.9rem",
            }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(htmlBody),
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHtmlModal(false)}>
            {t("Common.close")}
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmModal
        show={showDeleteModal}
        title={t("ExpenseDetailPage.modals.delete.title")}
        subtitle={t("ExpenseDetailPage.modals.delete.subtitle")}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => {
          userProfileService.deleteExpenseEvent({
            expenseId: expenseId,
            callback: ({ err, data }) => {
              if (err) {
                toast.error(err);
                return;
              }
              ExpenseTrackingMetadataDB.deleteItemFromExpenseTrackingMetadata(expenseId);
              toast.success("Expense deleted successfully");
              loadItems();
              navigate("/ExpenseTracking");
            },
          });
          setShowDeleteModal(false);
        }}
      />
    </Container>
  );
};

export default ExpenseDetailPage;
